import { MAIN_FILTER_TREE } from '@/App.settings';
import SaveModeEnum from '@/enums/SaveModeEnum';
import TreeNode from '@/types/TreeNode';
import { ObjectUtils } from "@/components/commons/Utils";
const objectUtils = new ObjectUtils();
import { Store } from 'vuex';
import { NavigationMediator } from './NavigationMediator';

/** Médiateur entre stores VueX pour assurer la communication entre eux et certains composants */
export class StoreMediator {
	/** Instance VueX */
	private store!: Store<any>;
	private navigationMediator!: NavigationMediator;

	private constructor() {
		console.debug("StoreMediator créé une seule et unique fois.");
	}

	private static instance: StoreMediator;
	/** Singleton vers une instance de StoreMediator */
	public static GetInstance(): StoreMediator {
        // Si l'instance n'existe pas encore, on la crée
        if (!StoreMediator.instance)
            StoreMediator.instance = new StoreMediator();
        
        // On retourne l'instance existante
        return StoreMediator.instance;
    }

	/**
	 * @param store Définition du $store à utiliser
	 * @description On utilise une méthode d'initialisation pour éviter les problèmes de dépendances circulaires au moment du 'new Store()' */
	public Init(store: Store<any>) {
		this.store = store;
	}

	/** Affecte le navigation médiator pour avoir accès à ses fonctions */
	public SetNavigationMediator(mediator: NavigationMediator) {
		this.navigationMediator = mediator;
	}

	/** 
	 * Indique à tous les composants implémentation INodeSelector de sélectionner le noeud passé en paramètre
	 * @param node 	Noeud à sélectionner
	 * @description Appel via le NavigationMediator de SetNode(node) sur les composants implémentant INodeSelector
	 **/
	public async SelectNodeInComponents(node: TreeNode) {
		if (this.navigationMediator)
			await this.navigationMediator.SelectNode(node);
	}

	/** Indique au composant implémentant IFeatureManager qu'un nouvel objet est en cours de création
	 * @see FeatureContainer.Vue
	 */
	public async NewObject(objectType: string) {
		/** Ouverture de la fiche objet si client ==> @todo WTF ???!!! */
		if (objectType === 'Client')
			await this.store.dispatch("SetObjectDisplayingFeature", {});

		await this.navigationMediator.NewObject();
	}

	/** Sur création d'un nouvel équipement fantome => ouverture de la fiche objet + rafraichissement et sélection de l'arbo sur le nouvel équipement fantome */
	public async NewGhostEquipment(ghostId: string) {
		// Ouverture de la fiche objet
		await this.store.dispatch("SetObjectDisplayingFeature", {});

		// On lance un rafraichissement de la liste des noeuds suite création equipement fantôme
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: ghostId });
	}

	public async DeleteObject(nodeId: string, selectParentNode: boolean) {
        let idNodeToSelect: string = undefined;
        if (selectParentNode || nodeId == this.store.getters['ObjectStore/GetNodeId'])
            idNodeToSelect = this.store.getters[MAIN_FILTER_TREE + '/ParentNodeId']({ childNodeId: nodeId });
        else 
            idNodeToSelect = this.store.getters['ObjectStore/GetNodeId'];

		// Lance un rafraichissement de la liste des noeuds + sélection du noeud parent du noeud supprimé (si selectParentNode)
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: (selectParentNode ? idNodeToSelect : undefined), selectNode: selectParentNode });
	}

	public async AppreciateObject(nodeId: string) {
		// Rafraichissement de l'arborescence
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId, selectNode: true });
	}

	public async DepreciateObject(nodeId: string) {
		// Rafraichissement de l'arborescence
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId, selectNode: true });
	}

	public async DuplicateObject(nodeId: string) {
		// Recharge l'arborescence pour afficher le noeud dupliqué
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId, selectNode: true });
	}

	/** @todo Voir si currentSaveMode et currentVersionId doivent être passés en paramètre ou bien obtenu par getter sur ObjectStore */
	public async SaveObject(currentSaveMode: SaveModeEnum, currentVersionId: number, loadedVersionId: number, nodeId: string, name: string) {
		if (currentSaveMode === SaveModeEnum.NewObject) {
			// Lance un rafraichissement de la liste des noeuds avec le PersistentId récupéré du Save() côté server
			await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId });
		}
		else {
			// S'il s'agit de la version courante alors on rafraichit le nom du noeud dans l'arborescence (au cas où ce nom ait été modifié) et on met à jour le nom du SingleNode
			if (currentVersionId == loadedVersionId) {
				// Un rafraichissement de l'arborescence est lancé uniquement pour les équipements afin de prendre en compte les mises à jour d'affectations
				if (objectUtils.IsMeter(this.store.getters['ObjectStore/GetObjectType'])) {
					// Lance un rafraichissement de la liste des noeuds avec le PersistentId récupéré du Save() côté server
					await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId });
				}
				else {
					/* "forms[0].Sections[0].Fields[0].Value" contient le nom du formulaire. Il s'agit toujours du premier champ dans la première section du premier formulaire */
					await this.store.dispatch(MAIN_FILTER_TREE + '/UpdateNodeName', { nodeId: nodeId, newName: name });
				}
			}
		}
	}

	public async PasteObject(nodeId: string) {
		// Recharge l'arborescence pour afficher le noeud dupliqué s'il ne s'agit pas d'un équipement
		await this.store.dispatch(MAIN_FILTER_TREE + "/Refresh", { nodeIdToOpen: nodeId, selectNode: true });
	}

	public async RenameObject(nodeId: string, newName: string) {
		// Mise-à-jour de l'intitulé du noeud dans l'arborescence
		await this.store.dispatch(MAIN_FILTER_TREE + "/UpdateNodeName", { nodeId: nodeId, newName: newName });
	}
}